/**
 * API client transport minier (Phase 2 — Royal LT CI / Guinée).
 * Endpoints /v1/operations/transport/*
 */
import { apiClient, unwrap, unwrapPaginated } from './client';
import type {
  AlerteMaintenance, AnomalieCarburant, BonCarburant, BonTravail, Camion,
  CarburantDashboard, CauseBonTravail, DepotCarburant, Equipe,
  GraviteAnomalieCarburant, LieuBonTravail, MaintenanceDashboard, Mine,
  MouvementDepot, NiveauBonTravail, PlanningPreventif, PrioriteBonTravail,
  RapportDestinataire, RapportEnvoiLog, RapportJournalier, RapportMensuelCarburant,
  RapportMensuelMaintenance, RatioGraviteCarburant,
  StatutAnomalieCarburant, StatutBonCarburant, StatutBonTravail, StatutCamion,
  StatutEquipe, StatutPlanningPreventif, StatutVoyage, TransportDashboard,
  TypeBonTravail, TypeCamion, TypeIncident, TypeMouvementDepot, Voyage,
} from '@/types';
import type { Paginated } from './operations';

// ── Equipes ────────────────────────────────────────────────────────────────

export interface EquipeListParams {
  statut?: StatutEquipe;
  search?: string;
  page?:   number;
  limit?:  number;
}

export interface CreateEquipePayload {
  code:        string;
  nom:         string;
  chefNom?:    string;
  secteur?:    string;
  exploitant?: string;
  statut?:     StatutEquipe;
  notes?:      string;
}

export type UpdateEquipePayload = Partial<CreateEquipePayload>;

// ── Camions ────────────────────────────────────────────────────────────────

export interface CamionListParams {
  statut?:   StatutCamion;
  equipeId?: string;
  actif?:    boolean;
  search?:   string;
  page?:     number;
  limit?:    number;
}

export interface CreateCamionPayload {
  code:                  string;
  capaciteTonnes:        number;
  typeCamion?:           TypeCamion;
  chargeMaxTonnes?:      number;
  consoTheoriqueL100km?: number;
  immatriculation?:      string;
  marque?:               string;
  modele?:               string;
  chauffeurNom?:         string;
  equipeId?:             string;
  statut?:               StatutCamion;
  kmActuel?:             number;
  dateMiseEnService?:    string;
  prochainEntretien?:    string;
  notes?:                string;
  actif?:                boolean;
}

export type UpdateCamionPayload = Partial<CreateCamionPayload>;

// ── Mines ──────────────────────────────────────────────────────────────────

export interface MineListParams {
  actif?:  boolean;
  search?: string;
  page?:   number;
  limit?:  number;
}

export interface CreateMinePayload {
  code:                    string;
  nom:                     string;
  exploitant?:             string;
  portDestinationDefault?: string;
  notes?:                  string;
  actif?:                  boolean;
}

export type UpdateMinePayload = Partial<CreateMinePayload>;

// ── Voyages ────────────────────────────────────────────────────────────────

export interface VoyageListParams {
  date?:             string;
  dateFrom?:         string;
  dateTo?:           string;
  mineId?:           string;
  camionId?:         string;
  statut?:           StatutVoyage;
  chefEquipeUserId?: string;
  search?:           string;
  page?:             number;
  limit?:            number;
}

export interface CreateVoyagePayload {
  camionId:             string;
  chauffeurNom:         string;
  mineId:               string;
  portDestination:      string;
  heureDepartMine:      string;
  tonnageCharge:        number;
  numeroBonPesee:       string;
  kmDepart?:            number;
  heureArriveePort?:    string;
  heureDepartPort?:     string;
  kmRetour?:            number;
  statut?:              StatutVoyage;
  incidentType?:        TypeIncident;
  incidentDescription?: string;
  raisonAnnulation?:    string;
  notes?:               string;
}

export type UpdateVoyagePayload = Partial<CreateVoyagePayload>;

// ── API ────────────────────────────────────────────────────────────────────

export const transportApi = {
  listEquipes: async (params: EquipeListParams = {}): Promise<Paginated<Equipe>> => {
    const res = await apiClient.get('/operations/transport/equipes', { params });
    return unwrapPaginated<Equipe>(res);
  },
  getEquipe: async (id: string): Promise<Equipe> => {
    const res = await apiClient.get(`/operations/transport/equipes/${id}`);
    return unwrap<Equipe>(res);
  },
  createEquipe: async (payload: CreateEquipePayload): Promise<Equipe> => {
    const res = await apiClient.post('/operations/transport/equipes', payload);
    return unwrap<Equipe>(res);
  },
  updateEquipe: async (id: string, payload: UpdateEquipePayload): Promise<Equipe> => {
    const res = await apiClient.patch(`/operations/transport/equipes/${id}`, payload);
    return unwrap<Equipe>(res);
  },
  deleteEquipe: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/transport/equipes/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  listCamions: async (params: CamionListParams = {}): Promise<Paginated<Camion>> => {
    const res = await apiClient.get('/operations/transport/camions', { params });
    return unwrapPaginated<Camion>(res);
  },
  getCamion: async (id: string): Promise<Camion> => {
    const res = await apiClient.get(`/operations/transport/camions/${id}`);
    return unwrap<Camion>(res);
  },
  createCamion: async (payload: CreateCamionPayload): Promise<Camion> => {
    const res = await apiClient.post('/operations/transport/camions', payload);
    return unwrap<Camion>(res);
  },
  updateCamion: async (id: string, payload: UpdateCamionPayload): Promise<Camion> => {
    const res = await apiClient.patch(`/operations/transport/camions/${id}`, payload);
    return unwrap<Camion>(res);
  },
  deleteCamion: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/transport/camions/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // ── Mines ────────────────────────────────────────────────────────────────
  listMines: async (params: MineListParams = {}): Promise<Paginated<Mine>> => {
    const res = await apiClient.get('/operations/transport/mines', { params });
    return unwrapPaginated<Mine>(res);
  },
  getMine: async (id: string): Promise<Mine> => {
    const res = await apiClient.get(`/operations/transport/mines/${id}`);
    return unwrap<Mine>(res);
  },
  createMine: async (payload: CreateMinePayload): Promise<Mine> => {
    const res = await apiClient.post('/operations/transport/mines', payload);
    return unwrap<Mine>(res);
  },
  updateMine: async (id: string, payload: UpdateMinePayload): Promise<Mine> => {
    const res = await apiClient.patch(`/operations/transport/mines/${id}`, payload);
    return unwrap<Mine>(res);
  },
  deleteMine: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/transport/mines/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // ── Voyages ──────────────────────────────────────────────────────────────
  listVoyages: async (params: VoyageListParams = {}): Promise<Paginated<Voyage>> => {
    const res = await apiClient.get('/operations/transport/voyages', { params });
    return unwrapPaginated<Voyage>(res);
  },
  getVoyage: async (id: string): Promise<Voyage> => {
    const res = await apiClient.get(`/operations/transport/voyages/${id}`);
    return unwrap<Voyage>(res);
  },
  createVoyage: async (payload: CreateVoyagePayload): Promise<Voyage> => {
    const res = await apiClient.post('/operations/transport/voyages', payload);
    return unwrap<Voyage>(res);
  },
  updateVoyage: async (id: string, payload: UpdateVoyagePayload): Promise<Voyage> => {
    const res = await apiClient.patch(`/operations/transport/voyages/${id}`, payload);
    return unwrap<Voyage>(res);
  },
  deleteVoyage: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/transport/voyages/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // ── Dashboard ────────────────────────────────────────────────────────────
  getDashboard: async (date?: string): Promise<TransportDashboard> => {
    const res = await apiClient.get('/operations/transport/dashboard', { params: date ? { date } : {} });
    return unwrap<TransportDashboard>(res);
  },

  // ── Rapport journalier ───────────────────────────────────────────────────
  getRapport: async (date?: string): Promise<RapportJournalier> => {
    const res = await apiClient.get('/operations/transport/rapport', { params: date ? { date } : {} });
    return unwrap<RapportJournalier>(res);
  },

  // ── Carburant — Dashboard ────────────────────────────────────────────────
  getCarburantDashboard: async (date?: string): Promise<CarburantDashboard> => {
    const res = await apiClient.get('/operations/transport/carburant/dashboard', { params: date ? { date } : {} });
    return unwrap<CarburantDashboard>(res);
  },

  // ── Carburant — Bons ─────────────────────────────────────────────────────
  listBonsCarburant: async (params: BonCarburantListParams = {}): Promise<Paginated<BonCarburant>> => {
    const res = await apiClient.get('/operations/transport/carburant/bons', { params });
    return unwrapPaginated<BonCarburant>(res);
  },
  getBonCarburant: async (id: string): Promise<BonCarburant> => {
    const res = await apiClient.get(`/operations/transport/carburant/bons/${id}`);
    return unwrap<BonCarburant>(res);
  },
  createBonCarburant: async (payload: CreateBonCarburantPayload): Promise<BonCarburant> => {
    const res = await apiClient.post('/operations/transport/carburant/bons', payload);
    return unwrap<BonCarburant>(res);
  },
  updateBonCarburant: async (id: string, payload: UpdateBonCarburantPayload): Promise<BonCarburant> => {
    const res = await apiClient.patch(`/operations/transport/carburant/bons/${id}`, payload);
    return unwrap<BonCarburant>(res);
  },
  deleteBonCarburant: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/transport/carburant/bons/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // ── Carburant — Anomalies ────────────────────────────────────────────────
  listAnomaliesCarburant: async (params: AnomalieCarburantListParams = {}): Promise<Paginated<AnomalieCarburant>> => {
    const res = await apiClient.get('/operations/transport/carburant/anomalies', { params });
    return unwrapPaginated<AnomalieCarburant>(res);
  },
  updateAnomalieCarburant: async (id: string, payload: UpdateAnomalieCarburantPayload): Promise<AnomalieCarburant> => {
    const res = await apiClient.patch(`/operations/transport/carburant/anomalies/${id}`, payload);
    return unwrap<AnomalieCarburant>(res);
  },

  // ── Carburant — Dépôts ───────────────────────────────────────────────────
  listDepots: async (): Promise<DepotCarburant[]> => {
    const res = await apiClient.get('/operations/transport/carburant/depots');
    return unwrap<DepotCarburant[]>(res);
  },
  createDepot: async (payload: CreateDepotPayload): Promise<unknown> => {
    const res = await apiClient.post('/operations/transport/carburant/depots', payload);
    return unwrap(res);
  },
  updateDepot: async (id: string, payload: UpdateDepotPayload): Promise<unknown> => {
    const res = await apiClient.patch(`/operations/transport/carburant/depots/${id}`, payload);
    return unwrap(res);
  },
  deleteDepot: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/transport/carburant/depots/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // ── Carburant — Mouvements ───────────────────────────────────────────────
  listMouvements: async (params: MouvementListParams = {}): Promise<Paginated<MouvementDepot>> => {
    const res = await apiClient.get('/operations/transport/carburant/mouvements', { params });
    return unwrapPaginated<MouvementDepot>(res);
  },
  createMouvement: async (payload: CreateMouvementPayload): Promise<unknown> => {
    const res = await apiClient.post('/operations/transport/carburant/mouvements', payload);
    return unwrap(res);
  },
  deleteMouvement: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/transport/carburant/mouvements/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // ── Carburant — Rapport mensuel ──────────────────────────────────────────
  getRapportMensuelCarburant: async (year: number, month: number): Promise<RapportMensuelCarburant> => {
    const res = await apiClient.get('/operations/transport/carburant/rapport-mensuel', { params: { year, month } });
    return unwrap<RapportMensuelCarburant>(res);
  },

  // ── Maintenance — Dashboard ──────────────────────────────────────────────
  getMaintenanceDashboard: async (date?: string): Promise<MaintenanceDashboard> => {
    const res = await apiClient.get('/operations/transport/maintenance/dashboard', { params: date ? { date } : {} });
    return unwrap<MaintenanceDashboard>(res);
  },

  // ── Maintenance — Bons de Travail ────────────────────────────────────────
  listBonsTravail: async (params: BonTravailListParams = {}): Promise<Paginated<BonTravail>> => {
    const res = await apiClient.get('/operations/transport/maintenance/bons-travail', { params });
    return unwrapPaginated<BonTravail>(res);
  },
  getBonTravail: async (id: string): Promise<BonTravail> => {
    const res = await apiClient.get(`/operations/transport/maintenance/bons-travail/${id}`);
    return unwrap<BonTravail>(res);
  },
  createBonTravail: async (payload: CreateBonTravailPayload): Promise<BonTravail> => {
    const res = await apiClient.post('/operations/transport/maintenance/bons-travail', payload);
    return unwrap<BonTravail>(res);
  },
  updateBonTravail: async (id: string, payload: UpdateBonTravailPayload): Promise<BonTravail> => {
    const res = await apiClient.patch(`/operations/transport/maintenance/bons-travail/${id}`, payload);
    return unwrap<BonTravail>(res);
  },
  deleteBonTravail: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/transport/maintenance/bons-travail/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // ── Planning préventif ───────────────────────────────────────────────────
  getAlertesPlanning: async (): Promise<AlerteMaintenance[]> => {
    const res = await apiClient.get('/operations/transport/maintenance/planning/alertes');
    return unwrap<AlerteMaintenance[]>(res);
  },
  listPlanning: async (params: PlanningListParams = {}): Promise<Paginated<PlanningPreventif>> => {
    const res = await apiClient.get('/operations/transport/maintenance/planning', { params });
    return unwrapPaginated<PlanningPreventif>(res);
  },
  genererBTPlanning: async (planningId: string, mecanicienPrincipalNom: string): Promise<BonTravail> => {
    const res = await apiClient.post(`/operations/transport/maintenance/planning/${planningId}/generer-bt`, { mecanicienPrincipalNom });
    return unwrap<BonTravail>(res);
  },
  annulerPlanning: async (planningId: string): Promise<unknown> => {
    const res = await apiClient.patch(`/operations/transport/maintenance/planning/${planningId}/annuler`, {});
    return unwrap(res);
  },
  triggerN2Hebdo: async (): Promise<{ created: number }> => {
    const res = await apiClient.post('/operations/transport/maintenance/planning/trigger-n2', {});
    return unwrap<{ created: number }>(res);
  },

  // ── Rapport mensuel maintenance ──────────────────────────────────────────
  getRapportMensuelMaintenance: async (year: number, month: number): Promise<RapportMensuelMaintenance> => {
    const res = await apiClient.get('/operations/transport/maintenance/rapport-mensuel', { params: { year, month } });
    return unwrap<RapportMensuelMaintenance>(res);
  },

  // ── Destinataires rapport journalier + envoi ─────────────────────────────
  listDestinataires: async (): Promise<RapportDestinataire[]> => {
    const res = await apiClient.get('/operations/transport/rapport/destinataires');
    return unwrap<RapportDestinataire[]>(res);
  },
  createDestinataire: async (payload: { email: string; fullName?: string; role?: string; actif?: boolean }): Promise<RapportDestinataire> => {
    const res = await apiClient.post('/operations/transport/rapport/destinataires', payload);
    return unwrap<RapportDestinataire>(res);
  },
  updateDestinataire: async (id: string, payload: { fullName?: string; role?: string; actif?: boolean }): Promise<RapportDestinataire> => {
    const res = await apiClient.patch(`/operations/transport/rapport/destinataires/${id}`, payload);
    return unwrap<RapportDestinataire>(res);
  },
  deleteDestinataire: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/transport/rapport/destinataires/${id}`);
    return unwrap<{ deleted: true }>(res);
  },
  sendRapportNow: async (date?: string): Promise<RapportEnvoiLog> => {
    const res = await apiClient.post('/operations/transport/rapport/send-now', null, { params: date ? { date } : {} });
    return unwrap<RapportEnvoiLog>(res);
  },
  listEnvoiLogs: async (): Promise<RapportEnvoiLog[]> => {
    const res = await apiClient.get('/operations/transport/rapport/logs');
    return unwrap<RapportEnvoiLog[]>(res);
  },
};

export interface PlanningListParams {
  dateFrom?: string;
  dateTo?:   string;
  niveau?:   NiveauBonTravail;
  statut?:   StatutPlanningPreventif;
  page?:     number;
  limit?:    number;
}

export interface BonTravailListParams {
  statut?:        StatutBonTravail;
  type?:          TypeBonTravail;
  camionId?:      string;
  lieu?:          LieuBonTravail;
  mecanicienNom?: string;
  dateFrom?:      string;
  dateTo?:        string;
  coutMinFcfa?:   number;
  search?:        string;
  page?:          number;
  limit?:         number;
}

export interface LignePieceBTPayload {
  designation:      string;
  reference?:       string;
  qte:              number;
  prixUnitaireFcfa: number;
  notes?:           string;
}

export interface CreateBonTravailPayload {
  camionId:                string;
  signaleParNom?:          string;
  type:                    TypeBonTravail;
  niveau:                  NiveauBonTravail;
  lieu:                    LieuBonTravail;
  descriptionPanne:        string;
  cause?:                  CauseBonTravail;
  mecanicienPrincipalNom:  string;
  mecaniciensAdditionnels?: string[];
  chefParcNom?:            string;
  priorite?:               PrioriteBonTravail;
  lignes?:                 LignePieceBTPayload[];
  heureDebut?:             string;
  heureFin?:               string;
  tauxHoraireFcfa?:        number;
  coutExterneFcfa?:        number;
  notes?:                  string;
}

export interface UpdateBonTravailPayload extends Partial<Omit<CreateBonTravailPayload, 'camionId'>> {
  travauxEffectues?:     string;
  testEffectue?:         boolean;
  testDescription?:      string;
  remiseEnServiceAt?:    string;
  kilometrageClotureKm?: number;
  photosUrls?:           string[];
  signedByMecanicien?:   boolean;
  signedByChefParc?:     boolean;
  statut?:               StatutBonTravail;
  raisonAnnulation?:     string;
}

export interface CreateDepotPayload {
  mineId:           string;
  capaciteTotaleL?: number;
  responsableNom?:  string;
  notes?:           string;
  actif?:           boolean;
}

export type UpdateDepotPayload = Partial<Omit<CreateDepotPayload, 'mineId'>>;

export interface MouvementListParams {
  depotId?:  string;
  type?:     TypeMouvementDepot;
  dateFrom?: string;
  dateTo?:   string;
  page?:     number;
  limit?:    number;
}

export interface CreateMouvementPayload {
  depotId:   string;
  type:      TypeMouvementDepot;
  volumeL:   number;
  dateEffet: string;
  source?:   string;
  notes?:    string;
}

// ── Types params ────────────────────────────────────────────────────────────

export interface BonCarburantListParams {
  date?:             string;
  dateFrom?:         string;
  dateTo?:           string;
  mineId?:           string;
  camionId?:         string;
  statut?:           StatutBonCarburant;
  gravite?:          RatioGraviteCarburant;
  anomalieSeulement?: boolean;
  search?:           string;
  page?:             number;
  limit?:            number;
}

export interface CreateBonCarburantPayload {
  mineId:                 string;
  camionId:               string;
  chauffeurNom:           string;
  dateHeure:              string;
  kmCompteur:             number;
  qteDemandeeL:           number;
  qteServieL:             number;
  raisonEcart?:           string;
  agentCarburantNom?:     string;
  photoCompteurUrl?:      string;
  signedByChauffeur?:     boolean;
  signedByAgent?:         boolean;
  notes?:                 string;
}

export type UpdateBonCarburantPayload = Partial<CreateBonCarburantPayload> & { statut?: StatutBonCarburant };

export interface AnomalieCarburantListParams {
  statut?:   StatutAnomalieCarburant;
  gravite?:  GraviteAnomalieCarburant;
  recidive?: boolean;
  page?:     number;
  limit?:    number;
}

export interface UpdateAnomalieCarburantPayload {
  statut?:                    StatutAnomalieCarburant;
  assignedToUserId?:          string;
  commentairesInvestigation?: string;
  decisionFinale?:            string;
}
